import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface ScoringRule {
  id: string;
  eventType: string;
  pointValue: number;
  multiplier: number;
  description: string;
  enabled: boolean;
  createdAt: Date;
}

const ScoringRules: React.FC = () => {
  const { t } = useTranslation();
  const [scoringRules, setScoringRules] = useState<ScoringRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ScoringRule | null>(null);
  const [form, setForm] = useState({
    eventType: '',
    pointValue: 10,
    multiplier: 1,
    description: '',
    enabled: true
  });

  useEffect(() => {
    loadScoringRules();
  }, []);

  const loadScoringRules = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'scoring_rules'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      })) as ScoringRule[];
      setScoringRules(data);
    } catch (error) {
      console.error('Error loading scoring rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...form,
        updatedAt: Timestamp.now()
      };

      if (editing) {
        await updateDoc(doc(db, 'scoring_rules', editing.id), data);
      } else {
        await addDoc(collection(db, 'scoring_rules'), {
          ...data,
          createdAt: Timestamp.now()
        });
      }

      setShowModal(false);
      setEditing(null);
      resetForm();
      loadScoringRules();
    } catch (error) {
      console.error('Error saving scoring rule:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('leaderboard.confirmDeleteScoring'))) {
      try {
        await deleteDoc(doc(db, 'scoring_rules', id));
        loadScoringRules();
      } catch (error) {
        console.error('Error deleting scoring rule:', error);
      }
    }
  };

  const handleEdit = (rule: ScoringRule) => {
    setEditing(rule);
    setForm({
      eventType: rule.eventType,
      pointValue: rule.pointValue,
      multiplier: rule.multiplier,
      description: rule.description,
      enabled: rule.enabled
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      eventType: '',
      pointValue: 10,
      multiplier: 1,
      description: '',
      enabled: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('leaderboard.scoringManagement')}</h3>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('leaderboard.newScoringRule')}
        </button>
      </div>

      <div className="space-y-3">
        {scoringRules.map(rule => (
          <div key={rule.id} className="card-dark p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">⭐</span>
                  <div>
                    <h4 className="text-lg font-bold text-white">{rule.eventType}</h4>
                    <p className="text-sm text-[#B8B8C3]">{rule.description}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-[#B8B8C3]">{t('leaderboard.pointValue')}</p>
                  <p className="text-2xl font-bold text-cyan-400">
                    {rule.pointValue} {t('leaderboard.points')}
                  </p>
                  {rule.multiplier > 1 && (
                    <p className="text-xs text-green-400">×{rule.multiplier} {t('leaderboard.multiplier')}</p>
                  )}
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  rule.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {rule.enabled ? t('leaderboard.enabled') : t('leaderboard.disabled')}
                </span>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(rule)}
                    className="btn-secondary text-sm"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(rule.id)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {scoringRules.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">⭐</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('leaderboard.noScoringRules')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('leaderboard.noScoringRulesDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('leaderboard.createFirstScoringRule')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('leaderboard.editScoringRule') : t('leaderboard.newScoringRule')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('leaderboard.eventType')}
                </label>
                <input
                  type="text"
                  value={form.eventType}
                  onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('leaderboard.eventTypePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('leaderboard.description')}
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-dark w-full"
                  rows={3}
                  placeholder={t('leaderboard.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.pointValue')}
                  </label>
                  <input
                    type="number"
                    value={form.pointValue}
                    onChange={(e) => setForm({ ...form, pointValue: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.multiplier')}
                  </label>
                  <input
                    type="number"
                    value={form.multiplier}
                    onChange={(e) => setForm({ ...form, multiplier: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="1"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="scoringEnabled"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="scoringEnabled" className="text-sm text-[#B8B8C3]">
                  {t('leaderboard.enableScoringRule')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoringRules;
